import {
  NodeStatusEvent,
  NodeEventType,
  NodeDataType,
  TerminationEvent,
  TerminationStatusCode,
  ExecutionResultEvent,
  WorkflowEventTypes,
  type NodeEvent,
} from '@/types/workflow-types';
import type {
  NodeResult,
  WorkflowRunWithResults,
} from '@/services/workflow/get-workflow-run';

/**
 * What `reconcileRunState` decided the caller should do.
 *
 * The helper is pure — it does not call any React setters or event
 * handlers itself.  It inspects the backend's view of the run and
 * tells the caller what synthetic events to replay + whether the
 * run is terminal / still active.  The caller routes the synthetic
 * events through the SAME handlers a live WebSocket event would hit,
 * so there's no parallel "reconcile" code path drifting away from
 * the live one over time.
 */
export interface ReconcileOutcome {
  /** Per-node synthetic events derived from `run.node_results`. */
  nodeEvents: Record<string, NodeStatusEvent[]>;
  /**
   * Node result payloads to hydrate via `updateNodeResult`.  Without
   * this, reconciled nodes get status badges (via `nodeEvents`) but
   * their output_data is missing from `node.data.result`, so the
   * output panel shows nothing.  Keyed by React Flow node ID.
   */
  nodeResultPayloads: { nodeId: string; data: Record<string, unknown> }[];
  /**
   * Synthetic terminal event to queue + route through
   * `handleExecutionResultEvent` / `handleTerminationEvent`.  Only
   * present when `run.status` is terminal (success / failed / stopped).
   *
   * We emit EITHER an EXECUTION_RESULT (success) OR a TERMINATION with
   * an error/stopped status code — the same shape the BE emits over
   * the live WS.  The matching `status_code` is what drives the
   * status bar into COMPLETED vs FAILED vs IDLE.
   */
  terminalEvent?:
    | { kind: 'execution_result'; event: ExecutionResultEvent }
    | { kind: 'termination'; event: TerminationEvent };
  /** True when `run.status` is success/failed/stopped. */
  isTerminal: boolean;
  /** True when `run.status === 'paused'`. */
  isPaused: boolean;
}

const TERMINAL_STATUSES = new Set(['success', 'failed', 'stopped']);

/**
 * Builds a deterministic event_id for a synthesised node event so
 * repeated reconciliations dedupe cleanly in `nodeStatuses` merges.
 */
function synthEventId(nodeResult: NodeResult): string {
  return `synth:node_result:${nodeResult.id}`;
}

function synthTerminalEventId(run: WorkflowRunWithResults): string {
  return `synth:terminal:${run.session_id}`;
}

/**
 * Map a NodeResult to a synthetic node event the canvas's status
 * derivation (``derive-node-status.ts``) can consume.
 *
 * Only `completed` and `failed` rows produce events — anything else
 * (queued, running, etc.) is non-terminal and the live stream should
 * own it.
 */
function synthesizeNodeEvent(result: NodeResult): NodeEvent | null {
  const status = result.status.toLowerCase();
  let eventType: NodeEventType | null = null;
  if (status === 'completed') {
    eventType = NodeEventType.EXECUTION_SUCCESSFUL;
  } else if (status === 'failed') {
    eventType = NodeEventType.EXECUTION_FAILED;
  }
  if (eventType === null) return null;

  const timestamp =
    result.completed_at ||
    result.started_at ||
    result.created_at ||
    new Date().toISOString();

  return {
    event_type: eventType,
    event_id: synthEventId(result),
    display_type:
      eventType === NodeEventType.EXECUTION_FAILED
        ? NodeDataType.ERROR
        : NodeDataType.NODE_RESULT,
    node_id: result.node_instance_id ?? result.node_id,
    node_display_name: '',
    dynamic_message: '',
    mode: 'block',
    block_id: synthEventId(result),
    timestamp,
    data: result.output_data ?? null,
    error: result.error_message ?? null,
  } as unknown as NodeEvent;
}

/**
 * Build a synthetic terminal event for the status bar to observe.
 *
 * `success` → EXECUTION_RESULT with `data.success = true`.
 * `failed`  → TERMINATION with `status_code = ERROR`.
 * `stopped` → TERMINATION with `status_code = STOPPED`.
 *
 * Routing through the same event handlers as the live stream means
 * `shouldShowCompleted` / `shouldShowFailed` light up identically.
 */
function synthesizeTerminalEvent(
  run: WorkflowRunWithResults,
): ReconcileOutcome['terminalEvent'] {
  const status = run.status.toLowerCase();
  const timestamp =
    run.completed_at || run.updated_at || new Date().toISOString();
  const event_id = synthTerminalEventId(run);

  if (status === 'success') {
    const event: ExecutionResultEvent = {
      event_type: WorkflowEventTypes.EXECUTION_RESULT as 'execution_result',
      event_id,
      timestamp,
      message: 'Reconciled from backend snapshot after reconnect',
      data: {
        success: true,
        message: '',
        session_id: run.session_id,
        id: run.workflow_id,
        // Minimal-but-valid shape: the editor reads
        // `data.workflow.nodes[*].node_result` to backfill node
        // result payloads.  We already backfill node results via
        // `nodeEvents` below, so an empty workflow.nodes array is
        // safe and keeps the synthesis surface small.
        workflow: { nodes: [], edges: [] },
        description: '',
        edges: [],
        entry_node_id: '',
        execution_metadata: '',
        execution_summary: {
          total_nodes: 0,
          errors: [],
          execution_order: [],
          entry_nodes: [],
        },
        global_context: '',
        metadata: '',
        name: '',
        nodes: [],
        websocket_url: '',
      },
    };
    return { kind: 'execution_result', event };
  }

  if (status === 'failed' || status === 'stopped') {
    const event: TerminationEvent = {
      event_type: WorkflowEventTypes.TERMINATION as 'termination',
      event_id,
      timestamp,
      status_code:
        status === 'stopped'
          ? TerminationStatusCode.STOPPED
          : TerminationStatusCode.ERROR,
    };
    return { kind: 'termination', event };
  }

  return undefined;
}

/**
 * Derive everything the editor needs to reconcile its local view of
 * a run against the authoritative backend snapshot returned by
 * `getRunBySession`.  Call this from every code path that can resume
 * into an already-in-progress (or silently-terminated) run:
 *
 *   1. `handleReconnectToLiveRun` — user refreshed or clicked a
 *      running/paused row in the sidebar.
 *   2. WebSocket reconnect mid-run — user closed their laptop, run
 *      finished while offline, WS reconnects but the stream is
 *      delta-only so no terminal event is replayed.
 *   3. Reconnection exhausted — `shouldCheckRunStatus` path where we
 *      already poll this endpoint.
 *
 * The returned synthetic events carry deterministic `event_id`s so
 * merging into `nodeStatuses` is idempotent — safe to call on every
 * reconnect, including transient ones.
 */
export function reconcileRunState(
  run: WorkflowRunWithResults,
): ReconcileOutcome {
  const status = run.status.toLowerCase();
  const isTerminal = TERMINAL_STATUSES.has(status);
  const isPaused = status === 'paused';

  const nodeEvents: Record<string, NodeStatusEvent[]> = {};
  const nodeResultPayloads: ReconcileOutcome['nodeResultPayloads'] = [];
  for (const result of run.node_results ?? []) {
    const key = result.node_instance_id ?? result.node_id;

    if (result.output_data) {
      nodeResultPayloads.push({ nodeId: key, data: result.output_data });
    }

    const event = synthesizeNodeEvent(result);
    if (!event) continue;
    if (!nodeEvents[key]) nodeEvents[key] = [];
    nodeEvents[key].push(event);
  }

  const terminalEvent = isTerminal ? synthesizeTerminalEvent(run) : undefined;

  return {
    nodeEvents,
    nodeResultPayloads,
    terminalEvent,
    isTerminal,
    isPaused,
  };
}

/**
 * Merge synthesised node events into the current `nodeStatuses` map
 * without introducing duplicates.  Dedup key is `event_id` — synthetic
 * events get a deterministic id so repeat reconciliations are no-ops,
 * and live events that happen to already carry the same id (same
 * NodeResult delivered via WS) are not double-counted.
 */
export function mergeNodeStatuses(
  current: Record<string, NodeStatusEvent[]>,
  incoming: Record<string, NodeStatusEvent[]>,
): Record<string, NodeStatusEvent[]> {
  let next: Record<string, NodeStatusEvent[]> | null = null;
  for (const [key, newEvents] of Object.entries(incoming)) {
    const existing = current[key] ?? [];
    const existingIds = new Set(
      existing
        .map((e) => (e as { event_id?: string }).event_id)
        .filter((id): id is string => typeof id === 'string'),
    );
    const additions = newEvents.filter((e) => {
      const id = (e as { event_id?: string }).event_id;
      return typeof id !== 'string' || !existingIds.has(id);
    });
    if (additions.length === 0) continue;
    if (!next) next = { ...current };
    next[key] = [...existing, ...additions];
  }
  return next ?? current;
}
